import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { RegistroService } from 'src/app/registro.service';
import { environment } from 'src/environments/environment';
import { years, months } from '../../reporte/modal-ventas/assets/date';

@Injectable({
    providedIn: 'root',
})
export class ReporteIndService {
    constructor(
        private readonly http: HttpClient,
        private readonly registroService: RegistroService
    ) {}

    apiUrl = environment.apiSiriUrl;

    /**
     * Obtiene los reportes existentes para la empresa actual,
     * filtrando por año y mes.
     *
     * @param year Año del reporte a consultar (en formato numérico o string).
     * @param month Mes del reporte a consultar (número del 1 al 12).
     * @returns {Promise<any[]>} Una promesa que contiene la lista de reportes encontrados.
     *
     * @example
     * ```typescript
     * const reportes = await reporteIndService.getReporteDate('2025', '5');
     * console.log(reportes);
     * ```
     */
    async getReporteDate(year: string, month: string) {
        const url = `${this.apiUrl}reporte/empresa-actual/${this.registroService.empresaSelected}/${year}/${month}`;
        try {
            const response = await firstValueFrom(this.http.get<any[]>(url));
            return response;
        } catch (error) {
            console.error(error);
        }
    }

    /**
     * Obtiene los años válidos para la carga de reportes.
     *
     * @remarks
     * - Retorna los años entre `ANIO_MINIMO` (2023) y el año actual.
     *
     * @returns {number[]} Lista de años válidos.
     *
     * @example
     * ```ts
     * const años = obtenerFechasValidas();
     * // → [2023, 2024, 2025]
     * ```
     */
    obtenerFechasValidas(): number[] {
        const fecha = new Date();
        const anioActual = new Date().getFullYear();
        const ANIO_MINIMO = 2025;
        const mesActual = fecha.getMonth() + 1;

        if (mesActual === 1) {
            return years.filter(
                (year) => year >= ANIO_MINIMO && year < anioActual
            );
        }
        return years.filter(
            (year) => year >= ANIO_MINIMO && year <= anioActual
        );
    }

    /**
     * Obtiene los meses válidos según el año seleccionado.
     *
     * @remarks
     * - Año actual → meses desde abril hasta el mes anterior al actual.
     * - Año 2025 → meses desde abril hasta el mes actual.
     * - Otros años → todos los meses.
     *
     * @param {number} [anioSeleccionado] Año seleccionado.
     * @returns {Array<{ name: string; number: number }>} Lista de meses válidos.
     *
     * @example
     * ```ts
     * const meses = obtenerMesesValidos(2025);
     * // → [{ name: 'Abril', number: 4 }, ..., { name: 'Noviembre', number: 11 }]
     * ```
     */
    obtenerMesesValidos(
        anioSeleccionado?: number | string
    ): Array<{ name: string; number: number }> {
        const fecha = new Date();
        const anioActual = fecha.getFullYear();
        const mesActual = fecha.getMonth() + 1;
        const MONTH_MINIMO = 4; // Abril
        const anio = Number(anioSeleccionado);

        if (anio === anioActual) {
            return months.filter((month) => month.number <= mesActual - 1);
        }

        // Año 2025 → desde abril hasta diciembre
        if (anio === 2025) {
            return months.filter((month) => month.number >= MONTH_MINIMO);
        }

        // Años futuros (2026+) → todos los meses
        if (anio > anioActual) {
            return months;
        }

        // Años anteriores → todos los meses
        return months;
    }

    /**
     * Valida si un agente vigilado puede reemplazar
     * un reporte de ventas para un año y mes específico.
     *
     * @param payload Datos necesarios para la validación
     * @returns Respuesta del backend con el estado y mensaje
     */
    validarReemplazo(payload) {
        return this.http.post<any>(
            `${this.apiUrl}reporte/validar-reemplazo`,
            payload
        );
    }
}
